import { Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import AdminSidebar from './AdminSidebar';

const PAGE_TITLES = {
  '/admin':                'Dashboard',
  '/admin/orders':         'Orders',
  '/admin/call-requests':  'Call Requests',
  '/admin/users':          'Users',
  '/admin/create-customer': 'Add Customer',
  '/admin/contacts':       'Contacts',
  '/admin/funnel':         'Visitor Funnel',
  '/admin/members':        'Members Directory',
  '/admin/ca-profiles':    'CA Profiles',
  '/admin/stalled-users':  'Stalled Users',
  '/admin/payments':       'Payments',
  '/admin/invoices':       'Invoices',
  '/admin/logs':           'Activity Logs',
  '/admin/customer-logs':  'Customer Logs',
  '/admin/ai-reports':     'AI Reports',
  '/admin/team':           'Team Management',
  '/admin/profile':        'My Profile',
};

function getTitle(pathname) {
  if (pathname.match(/^\/admin\/orders\/\d+/))        return 'Order Detail';
  if (pathname.match(/^\/admin\/users\/\d+/))         return 'User Detail';
  if (pathname.match(/^\/admin\/customer-logs\/\d+/)) return 'Customer Activity';
  return PAGE_TITLES[pathname] || 'Admin';
}

export default function AdminLayout() {
  const location = useLocation();
  const title    = getTitle(location.pathname);
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer on route change so navigating to a new page
  // doesn't leave the overlay sitting on top of the new content.
  useEffect(() => { setNavOpen(false); }, [location.pathname]);

  // Lock body scroll while the drawer is open on mobile.
  useEffect(() => {
    if (!navOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [navOpen]);

  return (
    <div className="flex min-h-screen bg-[#F5F4FB]">
      {/* Sidebar — fixed at desktop, slides in on mobile. AdminSidebar itself
          owns its `fixed left-0` positioning; we control visibility via
          translate so the same component covers both breakpoints. */}
      <div
        className={`fixed inset-y-0 left-0 z-40 transform transition-transform duration-300 lg:translate-x-0 ${
          navOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        <AdminSidebar onNavigate={() => setNavOpen(false)} />
      </div>

      {/* Mobile backdrop — only present below `lg` and only when open. */}
      {navOpen && (
        <button
          type="button"
          aria-label="Close menu"
          onClick={() => setNavOpen(false)}
          className="fixed inset-0 z-30 bg-black/40 backdrop-blur-sm lg:hidden"
        />
      )}

      {/* Main column — reclaims the left margin on small screens since the
          sidebar is no longer always taking 240px there. */}
      <div className="flex-1 min-w-0 lg:ml-[240px] flex flex-col min-h-screen">
        {/* Top bar */}
        <header className="sticky top-0 z-20 bg-white border-b border-[#E8E6F8] px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3 shadow-sm">
          <button
            type="button"
            onClick={() => setNavOpen((v) => !v)}
            aria-label={navOpen ? 'Close menu' : 'Open menu'}
            className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-[#E8E6F8] text-[#1A1A2E] hover:bg-[#F5F4FB] transition-colors"
          >
            {navOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
          <h1 className="text-base sm:text-lg font-heading font-bold text-[#1A1A2E] truncate">
            {title}
          </h1>
        </header>

        {/* Page content. min-w-0 + overflow-x-hidden stop any wide child (e.g. a
            long tracking URL) from forcing the whole content area to scroll
            horizontally; individual tables keep their own overflow-x-auto. */}
        <main className="flex-1 min-w-0 p-4 sm:p-6 overflow-x-hidden overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
